import { useState } from 'react';
import { Send, CheckCircle, Mail, Rocket } from 'lucide-react';
import { useSubscribeNewsletter } from '../functions/newsletterHook';
import '../index.css';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const { mutate, isLoading, isSuccess, isError, error: mutationError } = useSubscribeNewsletter();

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      mutate({ name: '', email });
      setEmail('');
    } catch (err) {
      setError('Subscription failed. Please try again.');
    }
  };

  return (

    <section className="py-24 px-4 bg-gradient-to-br from-navy-50 via-white to-navy-50/80">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-20 items-center">
          {/* Enhanced 3D Illustration */}
          {/* Illustration - Modern 3D Effect */}
<div className="relative flex justify-center group perspective-1000 hover:perspective-1000 transition-all duration-700">
  {/* Floating background elements */}
  <div className="absolute inset-0 flex justify-center items-center opacity-30">
    <div className="absolute w-64 h-64 bg-navy-600/10 rounded-full blur-2xl animate-float"></div>
    <div className="absolute w-48 h-48 bg-navy-700/10 rounded-full blur-xl animate-float-delayed"></div>
  </div>

  {/* Main card with 3D hover effect */}
  <div className="relative z-10 transform transition-transform duration-700 group-hover:rotate-y-12 group-hover:translate-z-20">
    <div className="p-8 bg-white/95 backdrop-blur-lg rounded-3xl shadow-2xl border-2 border-navy-100/30 hover:border-navy-200/50 transition-all">
      {/* Holographic effect container */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-navy-50 to-white">
        {/* Grid background */}
        <div className="absolute inset-0 opacity-20 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        
        {/* Floating mail icon with dynamic shadow */}
        <div className="relative z-10 p-8 flex justify-center items-center">
          <div className="absolute inset-0 bg-[conic-gradient(at_top_left,#0000ff10,#00ff0010,#ff000010)] opacity-30 animate-hue-rotate"></div>
          <div className="relative bg-gradient-to-br from-navy-700 to-navy-900 p-6 rounded-xl shadow-2xl transform transition-transform group-hover:scale-105">
            <Mail className="w-24 h-24 text-white/90 drop-shadow-xl" />
          </div>
        </div>

        {/* Animated connection dots */}
        <div className="absolute -top-4 -left-4 w-8 h-8 bg-amber-400 rounded-full blur-[2px] opacity-80 animate-pulse-delayed"></div>
        <div className="absolute -bottom-4 -right-4 w-8 h-8 bg-emerald-400 rounded-full blur-[2px] opacity-80 animate-pulse"></div>
      </div>

      {/* Floating badge with micro-interaction */}
      <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-navy-800 to-navy-700 text-white/95 px-5 py-2 rounded-full shadow-xl flex items-center gap-2 group-hover:-translate-y-1 transition-transform">
        <span className="relative flex h-3 w-3">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-400"></span>
        </span>
        <span className="text-sm font-medium tracking-wide">Join the Network</span>
      </div>
    </div>
  </div>

  {/* Subtle parallax effect elements */}
  <div className="absolute -top-16 -left-16 w-32 h-32 bg-navy-600/5 rounded-full blur-xl group-hover:translate-x-10 group-hover:translate-y-5 transition-transform duration-1000"></div>
  <div className="absolute -bottom-16 -right-16 w-32 h-32 bg-navy-700/5 rounded-full blur-xl group-hover:-translate-x-10 group-hover:-translate-y-5 transition-transform duration-1000"></div>
</div>

          {/* Content Section */}
          <div className="space-y-10">
            <div className="space-y-5 text-center md:text-left">
              <h2 className=" text-3xl lg:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-navy-800 via-navy-700 to-navy-600 leading-tight">
                Pioneer the Future
                <br />
                <span className="text-[#64FFDA] bg-clip-text text-transparent">
                  With Premium Insights
                </span>
              </h2>
              <p className="text-xl text-navy-600/85 max-w-2xl mx-auto md:mx-0 leading-relaxed">
                Access exclusive market intelligence, cutting-edge feature previews, 
                and members-only benefits directly in your inbox.
              </p>
            </div>

            {isSuccess ? (
              <div className="p-5 bg-emerald-50/95 rounded-xl border border-emerald-100/80 flex items-center gap-4 backdrop-blur-sm">
                <CheckCircle className="w-7 h-7 text-emerald-600/90 shrink-0" />
                <span className="text-emerald-700/95 font-medium text-lg">
                  Almost there! Confirm your subscription via email.
                </span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Professional email address"
                      className="w-full pl-6 pr-14 py-5 rounded-2xl border-2 border-navy-100/80 bg-white/95 focus:border-navy-300/90 focus:ring-2 focus:ring-navy-50/60 placeholder-navy-300/90 text-navy-900/95 font-medium transition-all shadow-sm hover:shadow-md"
                    />
                    <Mail className="w-6 h-6 text-navy-400/90 absolute right-6 top-1/2 -translate-y-1/2" />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`px-10 py-5 rounded-2xl font-semibold text-lg transition-all flex items-center justify-center gap-3
                      ${isLoading 
                        ? 'bg-navy-400/90 cursor-wait shadow-inner' 
                        : 'bg-navy-700 hover:bg-navy-800 active:scale-[97%] shadow-xl hover:shadow-2xl hover:shadow-navy-900/10'
                      } text-white/95`}
                  >
                    {isLoading ? (
                      <>
                        <div className="w-6 h-6 border-[3px] border-white/85 border-t-transparent rounded-full animate-spin" />
                        Securing Access...
                      </>
                    ) : (
                      <>
                        <Rocket className="w-6 h-6 -translate-y-px" />
                        Elevate My Experience
                      </>
                    )}
                  </button>
                </div>
                
                {/* Error Messages */}
                {(error || isError) && (
                  <div className="p-4 bg-rose-50/95 rounded-xl border border-rose-100/80 flex items-center gap-3 backdrop-blur-sm animate-enter-shake">
                    <span className="text-rose-600/90 text-xl">⚠️</span>
                    <span className="text-rose-700/95 text-sm leading-tight">
                      {error || mutationError?.response?.data?.message || 'Secure connection required. Please try again.'}
                    </span>
                  </div>
                )}

                <p className="text-sm text-navy-400/85 text-center md:text-left leading-relaxed">
                  <span className="opacity-90">256-bit encrypted commitment.</span>{' '}
                  <span className="opacity-75">One-click unsubscribe anytime.</span>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Newsletter;